import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getCategoryById } from "../api/CategoryAPI";
import { getWhyPageData, saveWhyOverride, whyScreenshotUrl, WhyPageData } from "../api/ProspectAPI";
import { logApiError, getUserMessage } from "../api/apiError";
import { Category } from "../types/Category";
import { CurrentListing, WhyComparisonBody, WhyPageFrame, WhyPainPoints, fillWhyTemplate, useWhyTexts } from "./WhyCategoryPage";

/** The admin's saved login, if any — the page is public, but a signed-in
 *  admin gets in-place editing. Same storage App.tsx uses. */
function savedAuthToken(): string {
    const token = localStorage.getItem("bh_auth_token");
    const expiresAt = Number(localStorage.getItem("bh_auth_expires_at") ?? 0);
    return token && expiresAt && Date.now() < expiresAt ? token : "";
}

/**
 * /why/p/{token}: the fully personalized comparison a prospect gets in their
 * invite email — THEIR current craigslist/eBay listing (fetched preview
 * image + title) next to THEIR category's real BabaHanks filters. Public but
 * token-gated; an unknown token degrades to the generic comparison.
 *
 * A signed-in admin can edit the heading/points right here: the page itself
 * is the live preview, and Save stores the per-prospect override.
 */
const WhyProspectPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [data, setData] = useState<WhyPageData | null>(null);
    const [category, setCategory] = useState<Category | null>(null);
    const [failed, setFailed] = useState(false);

    // In-place editing (admins only — detected via the saved login).
    const authToken = savedAuthToken();
    const [editing, setEditing] = useState(false);
    const [draftHeading, setDraftHeading] = useState("");
    const [draftPoints, setDraftPoints] = useState("");
    const [saving, setSaving] = useState(false);
    const [editMsg, setEditMsg] = useState<string | null>(null);

    useEffect(() => {
        if (!token) { setFailed(true); return; }
        getWhyPageData(token)
            .then((d) => {
                setData(d);
                if (d.categoryId) {
                    getCategoryById("", d.categoryId).then(setCategory).catch(() => {});
                }
            })
            .catch(() => setFailed(true));
    }, [token]);

    const listing: CurrentListing | null = data ? {
        url: data.listingUrl,
        title: data.listingTitle,
        imageUrl: data.listingImageUrl,
        description: data.listingDescription,
        screenshotUrl: data.hasScreenshot && token ? whyScreenshotUrl(token) : null,
    } : null;

    const catName = category?.name ?? data?.category ?? null;
    const globalTexts = useWhyTexts();
    // Per-prospect overrides beat the global templates; an override replaces
    // both bullet variants (it was written for this specific prospect).
    // While editing, the drafts drive the render — the page IS the preview.
    const effectiveHeading = editing
        ? (draftHeading || globalTexts.heading)
        : (data?.whyHeading || globalTexts.heading);
    const effectivePoints = editing ? draftPoints : data?.whyPoints;
    // Spread keeps the newer why1 fields (unused on this parked page) so the
    // object still satisfies WhyTexts.
    const texts = {
        ...globalTexts,
        heading: effectiveHeading,
        points: effectivePoints || globalTexts.points,
        pointsService: effectivePoints || globalTexts.pointsService,
    };

    const startEditing = () => {
        setDraftHeading(data?.whyHeading ?? "");
        setDraftPoints(data?.whyPoints ?? "");
        setEditMsg(null);
        setEditing(true);
    };

    const save = async () => {
        if (!token) return;
        setSaving(true);
        setEditMsg(null);
        try {
            await saveWhyOverride(authToken, token, {
                whyHeading: draftHeading,
                whyPoints: draftPoints,
            });
            setData((d) => (d ? {
                ...d,
                whyHeading: draftHeading.trim() || null,
                whyPoints: draftPoints.trim() || null,
            } : d));
            setEditing(false);
        } catch (e) {
            logApiError(e);
            setEditMsg(getUserMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const editorBar = authToken && data ? (
        <div className="why-cta-row" style={{ maxWidth: "1024px", margin: "0 auto 0.75rem", padding: "0 0.5rem" }}>
            {!editing ? (
                <div style={{ textAlign: "right" }}>
                    <button type="button" className="btn-sm" onClick={startEditing}
                            title="Visible to signed-in admins only — edit this prospect's heading and pain points right here">
                        ✎ Edit this page
                    </button>
                    {data.whyHeading || data.whyPoints
                        ? <span style={{ marginLeft: "0.5rem", fontSize: "0.8rem", color: "var(--text-secondary)" }}>customized ✓</span>
                        : null}
                </div>
            ) : (
                <div style={{ border: "2px solid var(--primary-color, #4f46e5)", borderRadius: "8px", padding: "0.75rem", background: "var(--bg-primary, #fff)" }}>
                    <strong style={{ display: "block", marginBottom: "0.35rem" }}>
                        Editing this prospect's why page — the page below updates as you type
                    </strong>
                    <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                        <label>Heading (blank = global template; {"{category}"}/{"{company}"} placeholders)</label>
                        <input value={draftHeading} onChange={(e) => setDraftHeading(e.target.value)} disabled={saving} />
                    </div>
                    <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                        <label>Pain points (blank = global; HTML or one bullet per line; {"{key_details}"})</label>
                        <textarea rows={6} value={draftPoints} onChange={(e) => setDraftPoints(e.target.value)} disabled={saving} />
                    </div>
                    <div className="button-group">
                        <button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
                        <button className="btn-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                    </div>
                    {editMsg && <div className="error-message" style={{ marginTop: "0.5rem" }}>{editMsg}</div>}
                </div>
            )}
        </div>
    ) : null;

    return (
        <WhyPageFrame
            heading={fillWhyTemplate(texts.heading, { category: catName, company: data?.orgName })
                || "Found, not buried"}
            intro={
                <>
                    {editorBar}
                    <WhyPainPoints category={category} texts={texts} />
                </>
            }
            showGenericLink={failed}
            feedback={{ token }}
        >
            <WhyComparisonBody
                category={category}
                listing={listing}
                initialLocation={data ? {
                    countryId: data.countryId,
                    admin1Id: data.admin1Id,
                    admin2Id: data.admin2Id,
                    cityId: data.cityId,
                } : null}
            />
        </WhyPageFrame>
    );
};

export default WhyProspectPage;